import { useEffect, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Button, Modal } from '@tes/ui';
import { api } from '../../lib/api';
import { messageOf } from '../../lib/errors';
import { checkQuickSend, toEnvelopeRequest } from '../../lib/quickSend';
import type { CreateEnvelopeBody, CreateEnvelopeResult } from '../../lib/types';
import { useQuickSend } from './quickSendStore';
import { DocumentList } from './DocumentList';
import { QuickSendCanvas } from './QuickSendCanvas';
import { FieldPanel } from './FieldPanel';
import { SendForm } from './SendForm';
import { SentView } from './SentView';

interface Review {
  body: CreateEnvelopeBody;
  /** One key per review: retrying THIS send replays it, an edited send is new. */
  idempotencyKey: string;
  warnings: string[];
}

/**
 * Quick send — upload your own PDFs, place signer fields, and send them to one
 * contact without making a template. Creates an ad-hoc envelope as the admin,
 * which marks it `source: dashboard`; n8n emails the link off envelope.sent.
 */
export default function QuickSendPage() {
  const qc = useQueryClient();
  const hasDocs = useQuickSend((s) => s.docs.length > 0);
  const [attempted, setAttempted] = useState(false);
  const [review, setReview] = useState<Review | null>(null);
  const [result, setResult] = useState<CreateEnvelopeResult | null>(null);

  // Is anything going to email the link? Unknown until the list loads.
  const webhooks = useQuery({ queryKey: ['webhooks'], queryFn: api.listWebhooks });
  const emailReady = webhooks.data?.some((w) => w.active && w.events.includes('envelope.sent'));

  const send = useMutation({
    mutationFn: (r: Review) => api.createEnvelope(r.body, r.idempotencyKey),
    onSuccess: (res) => {
      setResult(res);
      setReview(null);
      setAttempted(false);
      useQuickSend.getState().reset();
      void qc.invalidateQueries({ queryKey: ['envelopes'] });
    },
  });

  // Nothing persists: leaving and coming back starts a fresh send.
  useEffect(() => () => useQuickSend.getState().reset(), []);

  // Unsent work guard, both ways out (same pattern as the template editor).
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => hasDocs && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (!hasDocs) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasDocs]);

  const openReview = () => {
    setAttempted(true);
    const draft = useQuickSend.getState();
    const { errors, warnings } = checkQuickSend(draft);
    if (errors.length) return;
    send.reset();
    setReview({ body: toEnvelopeRequest(draft), idempotencyKey: crypto.randomUUID(), warnings });
  };

  if (result) {
    return <SentView result={result} emailReady={emailReady} onAnother={() => setResult(null)} />;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Quick send</h1>
        <p className="text-sm text-slate-500">
          Upload your own PDFs, place the fields, and send them to one person to sign — no template
          needed.
        </p>
      </div>

      {emailReady === false && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>
            No active webhook endpoint is subscribed to <code>envelope.sent</code>, so n8n won&apos;t
            email the signing link. Add that event to your n8n endpoint under Webhooks, or copy the
            link yourself after sending.
          </span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[15rem_minmax(0,1fr)_17rem]">
        <DocumentList />
        <div className="h-[72vh] overflow-hidden rounded-xl border border-slate-200 bg-white">
          <QuickSendCanvas />
        </div>
        <div className="max-h-[72vh] overflow-y-auto rounded-xl border border-slate-200 bg-white">
          <FieldPanel />
        </div>
      </div>

      <SendForm attempted={attempted} onReview={openReview} />

      {review && (
        <Modal
          title="Send for signing?"
          onClose={() => !send.isPending && setReview(null)}
          size="md"
          footer={
            <>
              <Button variant="ghost" onClick={() => setReview(null)} disabled={send.isPending}>
                Back
              </Button>
              <Button onClick={() => send.mutate(review)} disabled={send.isPending} data-autofocus>
                {send.isPending ? 'Sending…' : 'Send'}
              </Button>
            </>
          }
        >
          <ReviewSummary review={review} />
          {send.error && (
            <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {messageOf(send.error, 'Could not send')}
            </p>
          )}
        </Modal>
      )}

      {blocker.state === 'blocked' && (
        <Modal
          title="Leave without sending?"
          onClose={() => blocker.reset()}
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => blocker.reset()} data-autofocus>
                Stay
              </Button>
              <Button variant="danger" onClick={() => blocker.proceed()}>
                Discard
              </Button>
            </>
          }
        >
          <p className="text-sm text-slate-600">
            Your documents and fields haven&apos;t been sent. Leaving now throws them away.
          </p>
        </Modal>
      )}
    </div>
  );
}

function ReviewSummary({ review }: { review: Review }) {
  const { body, warnings } = review;
  const signer = body.signers[0];
  const fieldCount = body.documents.reduce((n, d) => n + d.fields.length, 0);
  return (
    <div className="space-y-3 text-sm text-slate-700">
      <p>
        {body.documents.length} document{body.documents.length === 1 ? '' : 's'} with {fieldCount}{' '}
        field{fieldCount === 1 ? '' : 's'} to <strong>{signer.name}</strong>{' '}
        <span className="text-slate-500">&lt;{signer.email}&gt;</span>.
      </p>
      <ol className="list-decimal space-y-0.5 pl-5">
        {body.documents.map((d) => (
          <li key={d.uploadId}>
            {d.title} <span className="text-slate-400">({d.fields.length} field{d.fields.length === 1 ? '' : 's'})</span>
          </li>
        ))}
      </ol>
      <div className="rounded-lg bg-slate-50 px-3 py-2">
        <div className="font-medium">{body.subject ?? '(no subject — n8n uses its default)'}</div>
        {body.message && <p className="mt-1 whitespace-pre-line text-slate-600">{body.message}</p>}
      </div>
      {warnings.length > 0 && (
        <ul className="list-disc space-y-0.5 rounded-lg bg-amber-50 py-2 pl-8 pr-3 text-amber-800">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
